import { EnvelopeOpenIcon } from "@phosphor-icons/react/dist/csr/EnvelopeOpen";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { useRequestSheetApprovals } from "./query";
import { RequestApprovalDialog } from "./request-approval-dialog";
import type { ISheetApprovalRequestState } from "./types";
import {
  getSheetApprovalActionMode,
  getSheetApprovalUnavailableMessage,
  SHEET_APPROVAL_ACTION_MODE,
  summarizeApprovalSend,
} from "./utils";

interface RequestApprovalActionProps {
  businessId: string | null;
  runId: string;
  state: ISheetApprovalRequestState | undefined;
  /** Gate state from the workbook payload — the only place it is computed. */
  approvalStatus: IPayrollSheetApprovalStatus | null | undefined;
}

/** Header action that asks every approver on this workbook to sign off. */
export const RequestApprovalAction = ({
  businessId,
  runId,
  state,
  approvalStatus,
}: RequestApprovalActionProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const requestApprovals = useRequestSheetApprovals(businessId);
  const mode = getSheetApprovalActionMode(state);

  if (mode === SHEET_APPROVAL_ACTION_MODE.hidden) return null;

  const handleConfirm = async (userIds: string[]) => {
    try {
      const response = await requestApprovals.mutateAsync({ runId, userIds });
      setIsDialogOpen(false);

      const { attempted, sent, skipped, failed } =
        summarizeApprovalSend(response);

      // Only an unexpected failure is an error. A skipped recipient was already
      // named in the dialog, so it rides along as a note on the success toast.
      if (failed > 0) {
        toast.error(
          sent === 0
            ? "Approval requests could not be sent"
            : `Sent ${sent} of ${attempted} approval requests`,
          {
            description: `${failed} could not be emailed. Check workbook access settings.`,
          },
        );
        return;
      }

      // Eligibility can change between opening the dialog and submitting, so a
      // successful response can contain nothing but skips.
      if (sent === 0) {
        toast.warning("No approval requests were sent", {
          description: `${skipped === 1 ? "The approver" : "All approvers"} turned out to be unreachable.`,
        });
        return;
      }

      toast.success(
        sent === 1 ? "Approval requested" : `Approval requested from ${sent}`,
        skipped > 0
          ? {
              description:
                skipped === 1
                  ? "1 approver was skipped, as shown."
                  : `${skipped} approvers were skipped, as shown.`,
            }
          : undefined,
      );
    } catch (error) {
      toast.error("Approval requests could not be sent", {
        description: getErrorMessage(error),
      });
    }
  };

  if (mode === SHEET_APPROVAL_ACTION_MODE.unreachable) {
    return (
      <Tooltip>
        {/* The wrapping span keeps the tooltip working: a disabled button
            swallows pointer events. */}
        <TooltipTrigger render={<span className="inline-flex" />}>
          <Button variant="secondary" disabled>
            <EnvelopeOpenIcon data-icon="inline-start" />
            Request approval
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {getSheetApprovalUnavailableMessage(state)}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setIsDialogOpen(true)}>
        <EnvelopeOpenIcon data-icon="inline-start" />
        Request approval
      </Button>
      {state && (
        <RequestApprovalDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onConfirm={handleConfirm}
          isSending={requestApprovals.isPending}
          state={state}
          approvalStatus={approvalStatus}
        />
      )}
    </>
  );
};
